import { supabase } from "@/shared/lib/supabaseClient";
import type { Database, EventoVisibilidade } from "@/shared/types/database.types";

/**
 * Camada de dados dos Eventos (Sprint 3, migration 0025).
 *
 * Regras: `ministerio_id` é OPCIONAL — null = Sede/Administração Geral. Um
 * evento com `ambiente_id` reserva o ambiente automaticamente (cria uma
 * reserva vinculada por `evento_id`), garantindo a prioridade de evento
 * público sobre reservas concorrentes (a restrição EXCLUDE do banco trava a
 * sobreposição). A autorização é do banco (RLS): admin, gestor do módulo
 * `eventos`, ou líder do ministério vinculado.
 */
// Estreitado na origem (não é o Row bruto): a coluna `visibilidade` usa CHECK
// constraint no Postgres, então o gerador do Supabase a tipa como `string`
// solto. Sobrescrevemos aqui para `EventoVisibilidade` para que qualquer
// consumidor deste tipo tenha a tipagem estrita sem precisar de cast pontual
// em cada ponto de uso.
export type Evento = Omit<Database["public"]["Tables"]["eventos"]["Row"], "visibilidade"> & {
  visibilidade: EventoVisibilidade;
};

export interface EventoComRelacoes extends Evento {
  ambiente_nome: string | null;
  /** Nome do ministério principal (primeiro da lista) — mantido para telas antigas. */
  ministerio_nome: string | null;
  /**
   * Todos os ministérios vinculados a este evento (F2-32, revisão
   * 20/07/2026: atividade conjunta entre ministérios). A coluna
   * `ministerio_id` continua guardando só o principal/primeiro, por
   * compatibilidade com notificações e a checagem de antecedência mínima.
   */
  ministerios: { id: string; nome: string }[];
  /**
   * Id da reserva de ambiente vinculada a este evento (evento_id), quando
   * existir. Usado para o formulário ignorar a própria reserva na checagem
   * de conflito ao editar (BUG-04, auditoria 20/07/2026) — sem isso, o
   * sistema acusava "ambiente ocupado" apontando pra reserva do próprio
   * evento em edição.
   */
  reserva_id: string | null;
}

export interface DadosEvento {
  titulo: string;
  descricao?: string | null;
  visibilidade: EventoVisibilidade;
  inicio: string; // ISO
  fim: string; // ISO
  diaInteiro: boolean;
  ambienteId?: string | null;
  /**
   * F2-32: um ou mais ministérios responsáveis. O primeiro da lista vira o
   * `ministerio_id` principal da linha; todos entram em `eventos_ministerios`.
   */
  ministerioIds?: string[];
  criadoPorPessoaId?: string | null;
}

function traduzErroEvento(error: { code?: string; message?: string } | null): Error {
  if (error?.code === "23514") {
    return new Error("O horário de término deve ser igual ou depois do início.");
  }
  // A20 (reteste 22-23/07/2026): a RLS barra criar/editar evento vinculado a um
  // ministério que a pessoa não lidera. Antes vinha um erro genérico — agora
  // explica o porquê.
  if (error?.code === "42501" || /row-level security/i.test(error?.message ?? "")) {
    return new Error(
      "Você não tem permissão para este evento. Só dá para criar/editar evento de um ministério que você lidera — ou eventos da administração da igreja, se você gerencia Eventos.",
    );
  }
  return new Error("Não foi possível salvar o evento.");
}

async function sincronizarReservaDoEvento(
  eventoId: string,
  dados: DadosEvento,
): Promise<{ code?: string } | null> {
  // v1 (simplicidade): apaga a reserva vinculada e recria se houver ambiente.
  // O erro do delete é checado: se a reserva antiga não sair, o insert abaixo
  // criaria uma SEGUNDA reserva órfã em vez de substituir a existente
  // (bug relatado: reserva com dados antigos "presa" mesmo após editar o evento).
  const { error: erroDelete } = await supabase.from("reservas").delete().eq("evento_id", eventoId);
  if (erroDelete) return erroDelete;
  if (!dados.ambienteId) return null;
  const { data: reserva, error } = await supabase
    .from("reservas")
    .insert({
      ambiente_id: dados.ambienteId,
      titulo: dados.titulo,
      inicio: dados.inicio,
      fim: dados.fim,
      ministerio_id: dados.ministerioIds?.[0] ?? null,
      evento_id: eventoId,
      solicitada_por_pessoa_id: dados.criadoPorPessoaId ?? null,
      status: "confirmada",
    })
    .select("id")
    .single();
  if (error) return error;
  // F4-05: espelha na reserva os MESMOS ministérios vinculados ao evento
  // (reservas_ministerios) — sem isso a reserva criada pelo evento aparece como
  // "Sede". O delete acima já limpou os vínculos antigos (FK reserva_id ON DELETE CASCADE).
  const idsMinisterios = dados.ministerioIds ?? [];
  if (reserva && idsMinisterios.length) {
    const { error: erroVinculos } = await supabase
      .from("reservas_ministerios")
      .insert(
        idsMinisterios.map((ministerioId) => ({ reserva_id: reserva.id, ministerio_id: ministerioId })),
      );
    if (erroVinculos) return erroVinculos;
  }
  return null;
}

/**
 * F2-32: grava a lista completa de ministérios vinculados ao evento em
 * `eventos_ministerios` (delete-then-insert, mesmo padrão usado para
 * categorias de livro na migration 0038). A coluna `ministerio_id` na linha
 * do evento já foi gravada separadamente com o primeiro da lista.
 */
async function sincronizarMinisteriosDoEvento(
  eventoId: string,
  ministerioIds: string[] | undefined,
): Promise<{ code?: string } | null> {
  // F5-14: sincronização INCREMENTAL — só remove os vínculos retirados e só
  // insere os novos. Editar um evento sem mexer nos ministérios não toca em
  // eventos_ministerios (evita o 42501 em evento com 2+ ministérios).
  const desejados = new Set(ministerioIds ?? []);
  const { data: atuais, error: erroLer } = await supabase
    .from("eventos_ministerios")
    .select("ministerio_id")
    .eq("evento_id", eventoId);
  if (erroLer) return erroLer;
  const existentes = new Set((atuais ?? []).map((r) => r.ministerio_id as string));
  const remover = [...existentes].filter((id) => !desejados.has(id));
  const adicionar = [...desejados].filter((id) => !existentes.has(id));
  if (remover.length) {
    const { error } = await supabase
      .from("eventos_ministerios")
      .delete()
      .eq("evento_id", eventoId)
      .in("ministerio_id", remover);
    if (error) return error;
  }
  if (adicionar.length) {
    const { error } = await supabase
      .from("eventos_ministerios")
      .insert(adicionar.map((ministerioId) => ({ evento_id: eventoId, ministerio_id: ministerioId })));
    if (error) return error;
  }
  return null;
}

export const eventosService = {
  /** Eventos não excluídos, com nome do ambiente e dos ministérios resolvidos. */
  async listar(): Promise<EventoComRelacoes[]> {
    const [eventosRes, reservasRes, ministeriosRes] = await Promise.all([
      supabase
        .from("eventos")
        .select("*, ambientes(nome), ministerios!eventos_ministerio_id_fkey(nome)")
        .is("deleted_at", null)
        .order("inicio", { ascending: true }),
      // BUG-04: mapa evento_id -> reserva_id, para o formulário de edição
      // saber qual reserva ignorar na checagem de conflito de ambiente.
      supabase.from("reservas").select("id, evento_id").not("evento_id", "is", null),
      // F2-32: todos os ministérios vinculados (não só o principal).
      supabase.from("eventos_ministerios").select("evento_id, ministerios(id, nome)"),
    ]);
    if (eventosRes.error) throw new Error("Não foi possível carregar os eventos.");

    const typed = (eventosRes.data ?? []) as unknown as Array<
      Evento & { ambientes: { nome: string } | null; ministerios: { nome: string } | null }
    >;
    const reservaPorEvento = new Map<string, string>();
    if (!reservasRes.error) {
      for (const r of (reservasRes.data ?? []) as Array<{ id: string; evento_id: string | null }>) {
        if (r.evento_id) reservaPorEvento.set(r.evento_id, r.id);
      }
    }
    const ministeriosPorEvento = new Map<string, { id: string; nome: string }[]>();
    if (!ministeriosRes.error) {
      for (const linha of (ministeriosRes.data ?? []) as unknown as Array<{
        evento_id: string;
        ministerios: { id: string; nome: string } | null;
      }>) {
        if (!linha.ministerios) continue;
        const lista = ministeriosPorEvento.get(linha.evento_id) ?? [];
        lista.push(linha.ministerios);
        ministeriosPorEvento.set(linha.evento_id, lista);
      }
    }
    return typed.map((e) => ({
      ...e,
      ambiente_nome: e.ambientes?.nome ?? null,
      ministerio_nome: e.ministerios?.nome ?? null,
      ministerios: ministeriosPorEvento.get(e.id) ?? [],
      reserva_id: reservaPorEvento.get(e.id) ?? null,
    }));
  },

  async criar(dados: DadosEvento): Promise<{ status: string }> {
    const { data: evento, error } = await supabase
      .from("eventos")
      .insert({
        titulo: dados.titulo,
        descricao: dados.descricao ?? null,
        visibilidade: dados.visibilidade,
        inicio: dados.inicio,
        fim: dados.fim,
        dia_inteiro: dados.diaInteiro,
        ambiente_id: dados.ambienteId ?? null,
        ministerio_id: dados.ministerioIds?.[0] ?? null,
        criado_por_pessoa_id: dados.criadoPorPessoaId ?? null,
        status: "agendado",
      })
      .select("id, status")
      .single();
    if (error) {
      // F4-04: rede de segurança do banco (trigger) — mensagem amigável caso
      // o formulário deixe passar um evento privado sem ministério.
      if (error.message?.includes("Evento privado precisa de um ministério")) {
        throw new Error("Evento privado precisa de um ministério responsável.");
      }
      throw traduzErroEvento(error);
    }
    if (!evento) throw traduzErroEvento(error);

    const erroMinisterios = await sincronizarMinisteriosDoEvento(evento.id, dados.ministerioIds);
    const erroReserva = await sincronizarReservaDoEvento(evento.id, dados);
    if (erroMinisterios || erroReserva) {
      // Desfaz o evento se o ambiente estiver em conflito ou os vínculos falharem.
      await supabase.from("eventos").delete().eq("id", evento.id);
      if (erroReserva?.code === "23P01") {
        throw new Error("Conflito de ambiente: já existe uma reserva nesse horário para este local.");
      }
      throw new Error("Não foi possível salvar os ministérios ou reservar o ambiente do evento.");
    }

    // Revisão 21/07/2026: antes, os líderes dos ministérios EXTRAS (F2-32,
    // vínculo múltiplo) só entravam no aviso de "novo evento" a partir da
    // primeira edição — na criação, o trigger da tabela eventos já dispara
    // ANTES de eventos_ministerios existir (é gravado logo acima, numa
    // chamada separada). Esta chamada fecha essa lacuna, avisando quem
    // ainda não foi avisado pelo trigger (líderes do ministério principal).
    // Falha aqui não derruba a criação do evento — notificação é best-effort.
    if (dados.ministerioIds && dados.ministerioIds.length > 1) {
      await supabase.rpc("notificar_lideres_evento_criado", {
        p_evento_id: evento.id,
        p_ministerio_ids: dados.ministerioIds,
      });
    }
    return { status: evento.status };
  },

  async atualizar(id: string, dados: DadosEvento): Promise<void> {
    // Guarda o estado anterior para poder desfazer o UPDATE do evento caso a
    // sincronização da reserva falhe — sem isso, o evento ficava com os
    // dados novos mas a reserva do ambiente sumia/ficava desatualizada
    // (bug relatado: reserva presa com o título/horário antigo do evento).
    const { data: anterior, error: erroAnterior } = await supabase
      .from("eventos")
      .select("titulo, descricao, visibilidade, inicio, fim, dia_inteiro, ambiente_id, ministerio_id")
      .eq("id", id)
      .single();
    if (erroAnterior || !anterior) throw new Error("Não foi possível carregar o evento atual.");

    const { error } = await supabase
      .from("eventos")
      .update({
        titulo: dados.titulo,
        descricao: dados.descricao ?? null,
        visibilidade: dados.visibilidade,
        inicio: dados.inicio,
        fim: dados.fim,
        dia_inteiro: dados.diaInteiro,
        ambiente_id: dados.ambienteId ?? null,
        ministerio_id: dados.ministerioIds?.[0] ?? null,
      })
      .eq("id", id);
    if (error) {
      if (error.message?.includes("Evento privado precisa de um ministério")) {
        throw new Error("Evento privado precisa de um ministério responsável.");
      }
      throw traduzErroEvento(error);
    }

    const erroMinisterios = await sincronizarMinisteriosDoEvento(id, dados.ministerioIds);
    const erroReserva = await sincronizarReservaDoEvento(id, dados);
    if (erroMinisterios || erroReserva) {
      // Desfaz o evento para não deixar título/data novos com a reserva do
      // ambiente órfã ou desatualizada.
      await supabase.from("eventos").update(anterior).eq("id", id);
      if (erroReserva?.code === "23P01") {
        throw new Error("Conflito de ambiente: já existe uma reserva nesse horário para este local.");
      }
      throw new Error("Não foi possível atualizar os ministérios ou a reserva do ambiente.");
    }
  },

  /**
   * Cancela um evento (soft — vai para o histórico) com motivo obrigatório.
   * Reteste 22-23/07/2026: substitui o antigo "excluir" (deleted_at + DELETE da
   * reserva, que falhava para o líder dono — F5-14b). Agora usa a RPC
   * cancelar_evento, que marca status='cancelado' e libera a sala CANCELANDO a
   * reserva vinculada (sem apagar nada).
   */
  async cancelar(id: string, motivo: string): Promise<void> {
    const { error } = await supabase.rpc("cancelar_evento", { p_evento_id: id, p_motivo: motivo });
    if (error) throw new Error(error.message || "Não foi possível cancelar o evento.");
  },

  /** F4-04: aprova um evento pendente (só quem gerencia o módulo Eventos). */
  async aprovar(id: string): Promise<void> {
    const { error } = await supabase.rpc("aprovar_evento", { p_evento_id: id });
    if (error) throw new Error(error.message || "Não foi possível aprovar o evento.");
  },

  /** F4-04: rejeita um evento pendente, com motivo (vai na notificação de quem criou). */
  async rejeitar(id: string, motivo: string): Promise<void> {
    const { error } = await supabase.rpc("rejeitar_evento", { p_evento_id: id, p_motivo: motivo });
    if (error) throw new Error(error.message || "Não foi possível recusar o evento.");
  },
};
